import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="custom-budget"
export default class extends Controller {
  static targets = [
    "categoryInput", "totalDisplay", "percentageDisplay", "submitButton",
    "remainingDisplay", "summaryCard", "progressBar", "categoryList",
    "addItemButton", "distributeAmount", "categoryPercentage", "allocatedLabel",
    "categoryRow", "tierGateModal"
  ]
  static values = {
    eventBudget: Number,
    originalTotal: Number,
    canAdd: Boolean,
    canDelete: Boolean
  }

  connect() {
    this.updateTotalAndPercentages()
    this.updateCategoryPercentages()
    this.validateInputs()
  }

  // Called when any category input changes
  inputChanged() {
    this.updateTotalAndPercentages()
    this.validateInputs()
    this.updateCategoryPercentages()
  }

  // Update total budget and overall percentage
  updateTotalAndPercentages() {
    const total = this.calculateTotal()
    const remaining = this.eventBudgetValue - total
    const percentage = this.eventBudgetValue > 0 ? ((total / this.eventBudgetValue) * 100) : 0
    const isOverBudget = total > this.eventBudgetValue

    // Update total display
    this.totalDisplayTargets.forEach(target => {
      target.textContent = this.formatCurrency(total)
    })

    // Update remaining balance display
    this.remainingDisplayTargets.forEach(target => {
      target.textContent = this.formatCurrency(remaining)
    })

    // Update percentage display
    this.percentageDisplayTargets.forEach(target => {
      target.textContent = `${percentage.toFixed(2)}%`
    })

    // Update allocated label
    if (this.hasAllocatedLabelTarget) {
      this.allocatedLabelTarget.textContent = `${this.formatCurrency(total)} allocated`
    }

    // Update distribute amount
    if (this.hasDistributeAmountTarget) {
      this.distributeAmountTarget.textContent = this.formatCurrency(Math.max(0, remaining))
    }

    // Toggle summary card states
    this.summaryCardTargets.forEach(card => {
      const valueEl = card.querySelector('[data-custom-budget-target="totalDisplay"], [data-custom-budget-target="remainingDisplay"], [data-custom-budget-target="percentageDisplay"]')
      if (isOverBudget) {
        card.classList.remove('bg-gray-50', 'border-gray-100')
        card.classList.add('bg-red-50', 'border-red-100')
        if (valueEl) {
          valueEl.classList.remove('text-gray-800')
          valueEl.classList.add('text-red-500')
        }
      } else {
        card.classList.remove('bg-red-50', 'border-red-100')
        card.classList.add('bg-gray-50', 'border-gray-100')
        if (valueEl) {
          valueEl.classList.remove('text-red-500')
          valueEl.classList.add('text-gray-800')
        }
      }
    })

    // Update progress bar
    if (this.hasProgressBarTarget) {
      const width = Math.min(percentage, 100)
      this.progressBarTarget.style.width = `${width}%`

      this.progressBarTarget.classList.remove('bg-emerald-600', 'bg-red-500')
      if (isOverBudget) {
        this.progressBarTarget.classList.add('bg-red-500')
      } else {
        this.progressBarTarget.classList.add('bg-emerald-600')
      }
    }
  }

  // Update individual category percentages
  updateCategoryPercentages() {
    this.categoryInputTargets.forEach(input => {
      const value = parseFloat(input.value) || 0
      const percentage = this.eventBudgetValue > 0 ? ((value / this.eventBudgetValue) * 100) : 0

      const row = input.closest('[data-custom-budget-target="categoryRow"]') || input.closest('.budget-category-item')
      if (row) {
        const percentageEl = row.querySelector('[data-custom-budget-target="categoryPercentage"]')
        if (percentageEl) {
          percentageEl.textContent = `${percentage.toFixed(2)}%`
        }
      }
    })
  }

  // Calculate total from all inputs
  calculateTotal() {
    return this.categoryInputTargets.reduce((total, input) => {
      return total + (parseFloat(input.value) || 0)
    }, 0)
  }

  // Validate inputs and enable/disable submit button
  validateInputs() {
    const total = this.calculateTotal()
    const hasNameErrors = this.hasCustomItemNameErrors()
    const isValid = total > 0 && !hasNameErrors

    this.submitButtonTargets.forEach(button => {
      button.disabled = !isValid
      button.classList.toggle('opacity-50', !isValid)
      button.classList.toggle('cursor-not-allowed', !isValid)
    })
  }

  // Reset to original AI budget
  resetToAI(event) {
    event.preventDefault()

    if (confirm('Are you sure you want to reset all budget categories to the AI recommendations? Custom items will be removed.')) {
      const form = this.element.querySelector('#reset-budget-form')
      if (form) {
        form.requestSubmit()
      }
    }
  }

  // Auto-distribute remaining budget
  autoDistribute() {
    const total = this.calculateTotal()
    const remaining = this.eventBudgetValue - total

    if (remaining <= 0) return

    const emptyInputs = this.categoryInputTargets.filter(input => !input.value || parseFloat(input.value) === 0)

    if (emptyInputs.length > 0) {
      const amountPerCategory = remaining / emptyInputs.length
      emptyInputs.forEach(input => {
        input.value = Math.round(amountPerCategory)
        input.dispatchEvent(new Event('input', { bubbles: true }))
      })
    } else {
      // Distribute proportionally among existing allocations
      const totalAllocated = total
      this.categoryInputTargets.forEach(input => {
        const currentValue = parseFloat(input.value) || 0
        const proportion = currentValue / totalAllocated
        const additionalAmount = remaining * proportion
        input.value = Math.round(currentValue + additionalAmount)
        input.dispatchEvent(new Event('input', { bubbles: true }))
      })
    }

    this.inputChanged()
  }

  // Add a custom budget item
  addItem() {
    if (!this.canAddValue) {
      this.openTierGateModal()
      return
    }

    const rowHtml = `
      <div class="budget-category-item px-4 sm:px-6 py-4 border-t border-gray-200" data-custom-budget-target="categoryRow">
        <div class="flex items-center justify-between">
          <div class="flex items-center min-w-0 flex-1">
            <div class="bg-gray-50 border border-gray-100 p-2 rounded-sm mr-3 flex-shrink-0">
              <i class="ph ph-package text-gray-600 text-lg"></i>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <input type="text"
                       class="custom-item-name-input text-sm font-medium text-gray-800 border border-gray-300 rounded-md px-2 py-1 w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                       placeholder="e.g. Emergency fund, Tips"
                       maxlength="40"
                       data-action="input->custom-budget#validateCategoryName blur->custom-budget#validateCategoryName" />
                <span class="text-gray-300">·</span>
                <span class="text-xs font-medium text-gray-500" data-custom-budget-target="categoryPercentage">0.00%</span>
              </div>
              <div class="custom-item-name-error text-[10px] text-red-500 mt-0.5 hidden"></div>
            </div>
          </div>
          <div class="flex-shrink-0 sm:hidden">
            <button type="button"
                    class="p-1.5 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors duration-200"
                    data-category-name=""
                    data-category-type="custom"
                    data-action="click->custom-budget#confirmDelete">
              <i class="ph ph-trash text-sm"></i>
            </button>
          </div>
        </div>
        <div class="flex items-center gap-2 mt-2 sm:mt-0 sm:justify-end pl-11 sm:pl-0">
          <span class="text-sm font-medium text-gray-600">₱</span>
          <input type="number"
                 name=""
                 value="0"
                 min="0"
                 step="1"
                 data-custom-budget-target="categoryInput"
                 data-action="input->custom-budget#inputChanged paste->custom-budget#handlePaste"
                 class="custom-item-amount-input flex-1 sm:w-44 sm:flex-none border border-gray-300 rounded-md px-3 py-2 text-base text-right focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                 placeholder="0" />
          <div class="hidden sm:block flex-shrink-0">
            <button type="button"
                    class="p-1.5 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors duration-200"
                    data-category-name=""
                    data-category-type="custom"
                    data-action="click->custom-budget#confirmDelete">
              <i class="ph ph-trash text-sm"></i>
            </button>
          </div>
        </div>
      </div>
    `

    if (this.hasCategoryListTarget) {
      this.categoryListTarget.insertAdjacentHTML('beforeend', rowHtml)

      // Focus the name input of the new row
      const newRow = this.categoryListTarget.lastElementChild
      const nameInput = newRow.querySelector('.custom-item-name-input')
      if (nameInput) {
        nameInput.focus()
      }
    }

    this.inputChanged()
  }

  // Validate custom category name
  validateCategoryName(event) {
    const nameInput = event.target
    const row = nameInput.closest('[data-custom-budget-target="categoryRow"]') || nameInput.closest('.budget-category-item')
    const errorEl = row?.querySelector('.custom-item-name-error')
    const amountInput = row?.querySelector('.custom-item-amount-input')
    const value = nameInput.value.trim()

    // Clear previous error
    nameInput.classList.remove('border-red-500')
    if (errorEl) {
      errorEl.classList.add('hidden')
      errorEl.textContent = ''
    }

    if (event.type === 'blur' && value === '') {
      nameInput.classList.add('border-red-500')
      if (errorEl) {
        errorEl.textContent = 'Category name is required'
        errorEl.classList.remove('hidden')
      }
      this.validateInputs()
      return
    }

    if (value !== '') {
      // Check for duplicates (case-insensitive)
      const existingNames = this.getExistingCategoryNames(nameInput)
      const isDuplicate = existingNames.some(name => name.toLowerCase() === value.toLowerCase())

      if (isDuplicate) {
        nameInput.classList.add('border-red-500')
        if (errorEl) {
          errorEl.textContent = 'This category already exists'
          errorEl.classList.remove('hidden')
        }
        this.validateInputs()
        return
      }

      // Update the amount input name attribute so it submits with the correct key
      if (amountInput) {
        amountInput.name = `custom_event_plan[custom_budget_breakdown][${value}]`
      }

      // Update the delete button's category name
      const deleteButton = row?.querySelector('[data-action*="confirmDelete"]')
      if (deleteButton) {
        deleteButton.dataset.categoryName = value
      }
    }

    this.validateInputs()
  }

  // Get all existing category names (from both fixed and custom items)
  getExistingCategoryNames(excludeInput) {
    const names = []

    // Get names from fixed category rows (those with no name input)
    this.element.querySelectorAll('[data-custom-budget-target="categoryRow"], .budget-category-item').forEach(row => {
      const nameInput = row.querySelector('.custom-item-name-input')
      if (nameInput) {
        // Custom item — include unless it's the one we're excluding
        if (nameInput !== excludeInput && nameInput.value.trim()) {
          names.push(nameInput.value.trim())
        }
      } else {
        // Fixed category — get name from the amount input's name attribute
        const amountInput = row.querySelector('[data-custom-budget-target="categoryInput"]')
        if (amountInput && amountInput.name) {
          const match = amountInput.name.match(/\[custom_budget_breakdown\]\[(.+)\]/)
          if (match) {
            names.push(match[1])
          }
        }
      }
    })

    return names
  }

  // Check if any custom item has name validation errors
  hasCustomItemNameErrors() {
    const nameInputs = this.element.querySelectorAll('.custom-item-name-input')
    for (const input of nameInputs) {
      if (input.value.trim() === '' || input.classList.contains('border-red-500')) {
        return true
      }
    }
    return false
  }

  // Format currency for display
  formatCurrency(amount) {
    return `₱ ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  // Handle form submission
  submitForm(event) {
    const total = this.calculateTotal()

    // Check for empty custom item names
    const nameInputs = this.element.querySelectorAll('.custom-item-name-input')
    for (const input of nameInputs) {
      if (input.value.trim() === '') {
        event.preventDefault()
        input.classList.add('border-red-500')
        const row = input.closest('[data-custom-budget-target="categoryRow"]') || input.closest('.budget-category-item')
        const errorEl = row?.querySelector('.custom-item-name-error')
        if (errorEl) {
          errorEl.textContent = 'Category name is required'
          errorEl.classList.remove('hidden')
        }
        input.focus()
        return false
      }
    }

    if (total === 0) {
      event.preventDefault()
      alert('Please allocate budget to at least one category.')
      return false
    }

    // Show confirmation if over budget
    if (total > this.eventBudgetValue) {
      const overAmount = total - this.eventBudgetValue
      const confirmMessage = `Your budget allocation (${this.formatCurrency(total)}) exceeds your event budget (${this.formatCurrency(this.eventBudgetValue)}) by ${this.formatCurrency(overAmount)}.\n\nSave anyway?`

      if (!confirm(confirmMessage)) {
        event.preventDefault()
        return false
      }
    }

    return true
  }

  // Handle number input formatting
  formatInput(event) {
    const input = event.target
    let value = input.value.replace(/[^\d]/g, '')

    const maxValue = this.eventBudgetValue * 10
    if (parseInt(value) > maxValue) {
      value = maxValue.toString()
    }

    input.value = value
    this.inputChanged()
  }

  // Handle paste events
  handlePaste(event) {
    setTimeout(() => {
      this.formatInput(event)
    }, 0)
  }

  // Confirm delete of a budget category
  confirmDelete(event) {
    if (!this.canDeleteValue) {
      this.openTierGateModal()
      return
    }

    const button = event.currentTarget
    const row = button.closest('[data-custom-budget-target="categoryRow"]') || button.closest('.budget-category-item')
    if (!row) return

    // Dismiss any active confirmation first
    if (this._confirmingRow && this._confirmingRow !== row) {
      this._restoreRow(this._confirmingRow)
    }

    const categoryName = button.dataset.categoryName
    const categoryType = button.dataset.categoryType

    // Store original content
    row._originalHTML = row.innerHTML

    const safeName = this._escapeHtml(categoryName)
    const message = categoryType === "ai"
      ? `Remove ${safeName}? You can restore it with Reset to AI Suggestion.`
      : `Remove ${safeName}? This cannot be undone.`

    row.innerHTML = `
      <div class="flex items-center justify-between px-6 py-4 w-full">
        <div class="flex items-center gap-2 text-sm text-red-700">
          <i class="ph ph-warning text-red-500"></i>
          <span>${message}</span>
        </div>
        <div class="flex items-center gap-2">
          <button type="button" class="text-xs font-medium text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded-md hover:bg-gray-100"
                  data-action="click->custom-budget#cancelDelete">Cancel</button>
          <button type="button" class="text-xs font-semibold text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded-md"
                  data-action="click->custom-budget#executeDelete">Remove</button>
        </div>
      </div>
    `
    row.classList.add('bg-red-50')
    this._confirmingRow = row
  }

  // Execute the deletion
  executeDelete(event) {
    const row = event.currentTarget.closest('[data-custom-budget-target="categoryRow"]') || event.currentTarget.closest('.budget-category-item')
    if (!row) return

    row.remove()
    this._confirmingRow = null
    this.updateTotalAndPercentages()
    this.updateCategoryPercentages()
    this.validateInputs()
  }

  // Cancel deletion and restore the row
  cancelDelete(event) {
    const row = event.currentTarget.closest('[data-custom-budget-target="categoryRow"]') || event.currentTarget.closest('.budget-category-item')
    if (!row) return

    this._restoreRow(row)
    this._confirmingRow = null
  }

  // Escape HTML to prevent XSS
  _escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Restore a row to its original content
  _restoreRow(row) {
    if (row._originalHTML) {
      row.innerHTML = row._originalHTML
      row.classList.remove('bg-red-50')
      delete row._originalHTML
    }
  }

  // Open the tier gate upgrade modal
  openTierGateModal() {
    if (this.hasTierGateModalTarget) {
      this.tierGateModalTarget.classList.remove('hidden')
    }
  }

  // Close the tier gate upgrade modal
  closeTierGateModal() {
    if (this.hasTierGateModalTarget) {
      this.tierGateModalTarget.classList.add('hidden')
    }
  }

  // Prevent clicks inside modal from closing it
  stopModalPropagation(event) {
    event.stopPropagation()
  }
}
